import React, { useState, useEffect, Component } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Play, Pause, Square, Eye, Clock, Folder, AlertCircle, Loader2, Volume2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error Boundary to catch any React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('SharedIdea Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#fff8f5',
          padding: '20px'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '40px', 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            textAlign: 'center',
            maxWidth: '400px'
          }}>
            <h2 style={{ color: '#f97316', marginBottom: '16px' }}>Voice Ideias</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Para uma melhor experiência, abra este link no navegador.
            </p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#f97316',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple Logo without SVG gradients (more compatible)
const SimpleLogo = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <div style={{
      width: '32px',
      height: '32px',
      backgroundColor: '#f97316',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Volume2 size={16} color="white" />
    </div>
    <span style={{ fontWeight: 'bold', fontSize: '18px' }}>
      Voice <span style={{ color: '#f97316' }}>Ideias</span>
    </span>
  </div>
);

function SharedIdeaContent() {
  const { shareId } = useParams();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const fetchSharedIdea = async () => {
      try {
        const response = await axios.get(`${API}/shared/${shareId}`);
        setIdea(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Ideia não encontrada');
        } else if (err.response?.status === 410) {
          setError('Este link expirou');
        } else {
          setError('Erro ao carregar ideia');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedIdea();
  }, [shareId]);

  const playText = () => {
    if (!idea?.content || !('speechSynthesis' in window)) {
      alert('Reprodução de áudio não suportada neste navegador. Abra no Chrome para ouvir.');
      return;
    }

    if (isPlaying) {
      speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(idea.content);
    utterance.lang = 'pt-BR';
    utterance.rate = 1;
    
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    
    speechSynthesis.speak(utterance);
    setIsPlaying(true);
  };

  const formatExpiration = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = date - now;
      
      if (diff <= 0) return 'Expirado';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours >= 24) {
        const days = Math.floor(hours / 24);
        return `${days}d restante${days > 1 ? 's' : ''}`;
      }
      
      return `${hours}h ${minutes}m`;
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#fff8f5'
      }}>
        <Loader2 size={32} color="#f97316" className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#fff8f5',
        padding: '20px'
      }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '40px', 
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <AlertCircle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
          <h2 style={{ marginBottom: '8px' }}>{error}</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            {error === 'Este link expirou' 
              ? 'O link de compartilhamento não está mais disponível.'
              : 'Verifique se o link está correto.'}
          </p>
          <a 
            href="/"
            style={{
              display: 'inline-block',
              backgroundColor: '#f97316',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none'
            }}
          >
            Conhecer Voice Ideias
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff8f5' }}>
      {/* Header */}
      <header style={{ 
        borderBottom: '1px solid #eee', 
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: '16px 20px'
      }}>
        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <SimpleLogo />
          <span style={{ fontSize: '14px', color: '#666' }}>Ideia Compartilhada</span>
        </div>
      </header>

      {/* Content */}
      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* Title Section */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            padding: '24px',
            color: 'white'
          }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
              {idea.title}
            </h1>
            <div style={{ display: 'flex', gap: '16px', fontSize: '14px', opacity: 0.9 }}>
              {idea.category_name && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Folder size={14} />
                  {idea.category_name}
                </span>
              )}
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Eye size={14} />
                {idea.views} visualizações
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={14} />
                {formatExpiration(idea.expires_at)}
              </span>
            </div>
          </div>

          {/* Content Section */}
          <div style={{ padding: '24px' }}>
            <div style={{ 
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '20px',
              fontSize: '16px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              marginBottom: '24px',
              minHeight: '100px'
            }}>
              {idea.content}
            </div>

            {/* Play Button */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={playText}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  padding: '14px 28px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                {isPlaying ? (
                  <>
                    <Square size={20} />
                    Parar
                  </>
                ) : (
                  <>
                    <Play size={20} />
                    Ouvir Ideia
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{ 
            backgroundColor: '#f8f9fa',
            padding: '16px 24px',
            borderTop: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Volume2 size={16} color="#f97316" />
              Capture suas ideias por voz
            </span>
            <a 
              href="/cadastro"
              style={{ fontSize: '14px', color: '#f97316', textDecoration: 'none', fontWeight: '500' }}
            >
              Criar conta grátis
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function SharedIdea() {
  return (
    <ErrorBoundary>
      <SharedIdeaContent />
    </ErrorBoundary>
  );
}
